import React from 'react';
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  ArrowRight,
  Calendar,
  Flame,
  GasPump as Fuel,
  Gear as Cog,
  Gauge,
  MapPin,
  CircleNotch as Loader2,
  Check,
  ShieldCheck,
  Info,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
} from '@phosphor-icons/react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CardRibbon } from '../components/ui/card-ribbon';
import { Card, CardContent } from '../components/ui/card';
import { useCars } from '../context/CarsContext';
import {
  useDutyFree,
  getMinDeposit,
  getFinancedAmount,
} from '../context/DutyFreeContext';
import { ClassifiedsSync } from './shared/ClassifiedsSync';
import { Breadcrumbs } from './shared/Breadcrumbs';
import { Slider } from '../components/ui/slider';
import { useVoiceAssistant } from '../context/VoiceAssistantContext';
import DutyFreeCheckbox from '../components/DutyFreeCheckbox';
import CarImagePlaceholder from '../components/CarImagePlaceholder';
import { formatRegYear } from '../utils/registrationYear';
import { formatDeliveryDate } from '../utils/deliveryDate';
import { useContactInfo } from '../hooks/useContactInfo';
import { generateImgproxyUrl } from '../services/imgproxy';
import { config } from '../config/runtimeConfig';
import LeadFormEmbed from '../components/registry/LeadFormEmbed';
import ShareButton from '../components/ShareButton';
import {
  CtaType,
  emitCtaClick,
  setCurrentCar,
} from '../services/carEngagementEvents';
import { parseEngineCC } from '../utils/engine';
import { usePhotoGallery } from '../hooks/usePhotoGallery';
import { carTitle } from '../utils/carTitle';
import './CarDetailPage.css';
import { VehicleEfficiency } from '../components/vehicle/VehicleEfficiency';
import { AvailabilityStatus } from '../components/vehicle/AvailabilityStatus';
import { FinanceCalculator, FinanceTeaser } from '../components/vehicle/FinanceCalculator';
import {
  SHOWCASE_FINANCE,
  calculateFinance,
  depositRange,
} from '../utils/financeCalculator';
import { formatEuro, numberLocale } from '../utils/formatMoney';
import { useTranslation } from '../i18n/I18nContext';
import { useHidePillOver } from '../hooks/useHidePillOver';
import { useShowcaseText } from '../i18n/useShowcaseText';
import { useTranslateVehicleValue } from '../utils/vehicleValues';

// MODX FormIt email chunks on client sites are typically wired as
// `[[++site_url]][[+url]]` — they expect a RELATIVE path. Sending
// window.location.href (absolute) produces duplicated domains in emails.
// Also strip ad-tracking query params so dealer-facing links stay clean.
/**
 * "+reg" price suffix with an explicit (i) info icon trigger — mirrors
 * the DutyFreeCheckbox pattern so both tooltips on this page look and
 * behave the same.
 *
 * - "+reg" stays as inert text next to the price (no longer a button).
 * - A separate Info icon next to it triggers the tooltip on hover
 *   (desktop) and on tap (mobile via click-toggle + click-outside).
 *
 * Uses `composedPath()` for click-outside because event.target is
 * retargeted to the shadow host when the widget runs in Shadow DOM,
 * so a plain `contains(e.target)` check would never match.
 */
const RegFeeLabel = ({ govFeeText }) => {
  const { t } = useTranslation();
  const showcaseText = useShowcaseText();
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (!ref.current) return;
      const insideRef =
        path.length > 0
          ? path.includes(ref.current)
          : ref.current.contains(e.target);
      if (!insideRef) setIsOpen(false);
    };
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [isOpen]);

  // Strip leading "* " — title now carries that semantic.
  const bodyText = govFeeText?.replace(/^\*\s*/, '') || '';

  return (
    <span className="car-detail__inline-note">
      {showcaseText('showcase.car.reg', '+reg')}
      <span className="car-detail__print-only car-detail__print-only--inline">
        *
      </span>
      <span className="car-detail__tooltip-host car-detail__no-print" ref={ref}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen((v) => !v);
          }}
          className="car-detail__plain-btn"
          aria-label={t(
            'showcase.car.regFeeInfo',
            'Show registration fee information'
          )}
        >
          <Info size={14} className="car-detail__icon car-detail__icon--help" />
        </button>
        {/* Tooltip popover — same visual + positioning as DutyFreeCheckbox */}
        <div
          className={`reg-fee-tooltip car-detail__tooltip${
            isOpen ? ' car-detail__tooltip--visible' : ''
          }`}
        >
          <p className="car-detail__strong">
            {t('showcase.car.regFee', 'Registration fee')}
          </p>
          <p>{bodyText}</p>
        </div>
      </span>
    </span>
  );
};

const CarDetailPage = () => {
  // В catalog-embed дилера провайдера нет — там `t()` отдаёт английский
  // fallback, то есть прежний текст. Переводится витрина (см. Catalog.jsx).
  const { t, language } = useTranslation();
  const translateVehicleValue = useTranslateVehicleValue();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // URL каталога с фильтрами, сохранённый в sessionStorage перед переходом на деталь
  const backTo = (() => {
    try {
      return sessionStorage.getItem('b2b-back-to-cars') || '/cars';
    } catch (_) {
      return '/cars';
    }
  })();
  // #828: mini-catalog hard-redirect передаёт `backTo=<страница-источник>` (напр.
  // /british-forces-cars), чтобы "Back to Cars" вернул туда, откуда открыли VDP,
  // а не на catalogUrl/cars. Чтим только same-origin (guard от open-redirect).
  const backToExternal = (() => {
    const raw = searchParams.get('backTo');
    if (!raw) return null;
    try {
      const u = new URL(raw, window.location.href);
      return u.origin === window.location.origin ? u.href : null;
    } catch (_) {
      return null;
    }
  })();
  const handleBackToCars = () => {
    if (backToExternal) {
      window.location.href = backToExternal;
      return;
    }
    navigate(backTo);
  };
  const [optimizedImages, setOptimizedImages] = React.useState({
    main: [],
    thumbnails: [],
  });
  // Shared gallery logic (index + swipe) — same hook powers the catalog card.
  // Swipe lets mobile users step through photos without tapping each thumbnail;
  // next/prev drive the desktop hover arrows. Vertical scroll is preserved via
  // touch-action: pan-y on the image wrapper.
  const {
    index: currentImageIndex,
    setIndex: setCurrentImageIndex,
    next: nextImage,
    prev: prevImage,
    swipeHandlers,
  } = usePhotoGallery(optimizedImages.main?.length || 0);
  const hasMultipleImages = (optimizedImages.main?.length || 0) > 1;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isFinanceModalOpen, setIsFinanceModalOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const { getCarById, loading, error } = useCars();
  const {
    isDutyFree,
    setIsDutyFree,
    calculateDutyFree,
    priceLabel,
    labelText,
    calculateMonthlyPayment,
    bfPackageTitle: bfPackageTitleRaw,
    bfPackageBadge1: bfPackageBadge1Raw,
    bfPackageBadge2: bfPackageBadge2Raw,
    bfPackageBadge3: bfPackageBadge3Raw,
    bfPackageDisclaimer: bfPackageDisclaimerRaw,
    bfPackageCtaText: bfPackageCtaTextRaw,
    govFeeText: govFeeTextRaw,
    bfMaxFinancing,
    bfBadgeColor,
    bfSliderColor,
  } = useDutyFree();
  // Тексты пакета финансирования приходят из конфига дилера; на своём стоке
  // витрины их значения по умолчанию — на языке покупателя (#1431).
  const showcaseText = useShowcaseText();
  const bfPackageTitle = showcaseText('showcase.car.bf.title', 'Finance this car', bfPackageTitleRaw);
  const bfPackageBadge1 = showcaseText('showcase.car.bf.badge1', 'ZERO DOWN OPTION**', bfPackageBadge1Raw);
  const bfPackageBadge2 = showcaseText('showcase.car.bf.badge2', 'BUY-BACK GUARANTEE', bfPackageBadge2Raw);
  const bfPackageBadge3 = showcaseText('showcase.car.bf.badge3', 'LOW CREDIT SCORE ACCEPTED', bfPackageBadge3Raw);
  const bfPackageDisclaimer = showcaseText(
    'showcase.car.bf.disclaimer',
    '* Finance based on DUTY FREE price for personal loan through Eurobank',
    bfPackageDisclaimerRaw
  );
  const bfPackageCtaText = showcaseText('showcase.car.bf.cta', 'Request an offer', bfPackageCtaTextRaw);
  const govFeeText = showcaseText('showcase.car.bf.govFee', '* Government fixed fee 150€', govFeeTextRaw);

  // Расчёт витринного калькулятора на момент «Request a finance offer» (#1335):
  // с ним уходит заявка. `null` — заявка с дилерского BF-блока.
  const [financeQuote, setFinanceQuote] = React.useState(null);
  // Раскрыт ли витринный калькулятор: пока раскрыт, его «Request a finance
  // offer» — главное действие, и кнопки страницы под ним прячутся (#1335).
  // Последний расчёт калькулятора — его показывает тизер в колонке цены.
  const [liveQuote, setLiveQuote] = React.useState(null);
  // Пилюля ассистента уступает место кнопкам действия, пока они в нижней
  // полосе экрана (тот же приём, что у формы героя на главной): на телефоне
  // она закрывала «Poser une question». Реф вешается только на витрине —
  // правило для класса есть лишь в `demo-shell.css` (#1335).
  // Ряд появляется после загрузки машины, а не при монтировании страницы,
  // поэтому элемент держим в состоянии: новый объект-реф перезапускает хук.
  const [ctaRowEl, setCtaRowEl] = React.useState(null);
  const ctaRowRef = React.useMemo(() => ({ current: ctaRowEl }), [ctaRowEl]);
  useHidePillOver(ctaRowRef);

  // BF accent colors — per-org configurable (#1077). Дефолты воспроизводят
  // текущий вид: бейджи следуют бренду, thumb слайдера чёрный.
  const bfBadgeColorResolved = bfBadgeColor || 'var(--theme-primary)';
  const bfSliderColorResolved = bfSliderColor || '#111827';
  const contactInfo = useContactInfo();
  // Issue #944 Phase 4: migrated to openChat (unified entry point).
  const { openChat } = useVoiceAssistant();

  const car = getCarById(id);

  // #828: mini-catalog hard-redirect переносит DF state через `?dutyFree=true`.
  // Открываем VDP с уже включённым BF toggle. Gate на config.SHOW_DUTY_FREE_FILTER:
  // на embed без DF параметр игнорируется (не подменяем цену стороннему embed'у).
  // Зависим только от searchParams — эффект не перетирает ручное выключение toggle.
  React.useEffect(() => {
    if (
      searchParams.get('dutyFree') === 'true' &&
      config.SHOW_DUTY_FREE_FILTER
    ) {
      setIsDutyFree(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Расчитываем цену для отображения (обычная или Duty Free из инвентаря)
  const displayPrice = car && isDutyFree ? calculateDutyFree(car) : car?.price;

  // BF finance deposit: финансирование капается на bfMaxFinancing (€12,000),
  // всё сверху обязан покрыть deposit → minDeposit = price - maxFinancing.
  const minDeposit = getMinDeposit(displayPrice, bfMaxFinancing);
  // deposit — источник истины для слайдера и расчётов (всегда валиден).
  // depositText — то, что видно в поле ввода (допускает промежуточный ввод).
  const [deposit, setDeposit] = React.useState(minDeposit);
  const [depositText, setDepositText] = React.useState(String(minDeposit));
  // Поле показывает форматированное число с разделителями, когда не в фокусе;
  // при редактировании — сырые цифры (чтобы каретка не прыгала).
  const [depositFocused, setDepositFocused] = React.useState(false);
  const depositDisplay = depositFocused
    ? depositText
    : depositText === ''
    ? ''
    : Number(depositText).toLocaleString();

  // Сброс deposit к минимуму, когда меняется цена (например, переключение Duty Free
  // или загрузка другого авто) — иначе старое значение может вылезти за диапазон.
  React.useEffect(() => {
    setDeposit(minDeposit);
    setDepositText(String(minDeposit));
  }, [minDeposit]);

  const financedAmount = getFinancedAmount(
    displayPrice,
    deposit,
    bfMaxFinancing
  );

  const monthlyPayment = calculateMonthlyPayment(financedAmount);

  const clampDeposit = (value) =>
    Math.min(Math.max(value, minDeposit), displayPrice || minDeposit);

  // Слайдер: значение всегда валидно — синхронизируем deposit и поле.
  const handleSliderChange = (raw) => {
    const clamped = clampDeposit(Math.round(Number(raw)));
    setDeposit(clamped);
    setDepositText(String(clamped));
  };

  // Ручной ввод: оставляем только цифры, даём печатать свободно, но deposit
  // (а значит slider + расчёт) держим в допустимом диапазоне.
  const handleDepositInput = (raw) => {
    const digits = String(raw).replace(/[^\d]/g, '');
    setDepositText(digits);
    if (digits !== '') setDeposit(clampDeposit(parseInt(digits, 10)));
  };

  // По уходу из поля приводим текст к зафиксированному (зажатому) значению.
  const handleDepositBlur = () => {
    setDepositFocused(false);
    const digits = depositText.replace(/[^\d]/g, '');
    const clamped = clampDeposit(
      digits === '' ? minDeposit : parseInt(digits, 10)
    );
    setDeposit(clamped);
    setDepositText(String(clamped));
  };

  // Old price из API (Google Sheets) или парсинг из названия как fallback
  const parseOldPriceFromModel = (model) => {
    if (!model) return null;
    const match = model.match(/Reduced from (\d+)€/i);
    return match ? parseInt(match[1]) : null;
  };
  const oldPrice = car?.oldPrice || parseOldPriceFromModel(car?.model);
  const displayOldPrice = oldPrice;

  // Заголовок: используем name из Google Sheets если есть, иначе формируем
  // из make + model. Год не добавляем — он уже показан в meta-табличке
  // под title (#911: дилеры жаловались на дубликат "Honda Vezel 2025"
  // + "Year: 2025" в карточке).
  const cleanMake = car?.make?.replace(/\*/g, '') || car?.make || '';
  const cleanModel = car?.model?.replace(/\*/g, '') || car?.model || '';
  const displayTitle = car?.name || `${cleanMake} ${cleanModel}`.trim();

  // Attrs for the two car-page lead forms (#1087). Both are template-driven
  // (resolve-by-code) LeadFormEmbed instances in MODAL presentation: the car
  // page owns the CTA button, mounts the form only while open (formModalOpen
  // auto-opens the sheet), and unmounts via onClose. Finance passes the real
  // financed figures via `formExtra` → the lead `extra` (LeadFormEmbed computes
  // the monthly with the dealer-configured calc). `{car}` in the title is
  // substituted with the car name by the renderer.
  //
  // MODX payload: `formModxFields` is a VERBATIM builder that reproduces Andys'
  // EXACT pre-#1087 FormIt payload (field keys, `€…` formatting, `formEnquire`
  // literals, `enquiry_type`). LeadFormEmbed sends it as-is — the generic shape
  // is NOT layered over it — so Andys' notification keeps working with zero
  // reconfiguration (interim; clearable once Resend notifications land). This is
  // SEPARATE from the CRM lead `extra`, which stays clean camelCase.
  const checkAvailabilityAttrs = React.useMemo(
    () => ({
      formTemplateCode: 'check_availability',
      formType: 'check_availability',
      formTitle: 'Check Availability for {car}',
      carId: car?.id,
      carTitle: displayTitle,
      presentation: 'modal',
      formModalOpen: true,
      // EXACT legacy Check-Availability MODX payload. Only posted when a form
      // template supplies its own `modxEndpoint`; the per-org endpoint was
      // removed (#1214) so the CA form now captures to CRM only.
      formModxFields: ({ formData, sourceUrl }) => ({
        formEnquire: 'Send Request',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || '',
        position: displayTitle || '',
        nospam: '',
        url: sourceUrl,
      }),
    }),
    [car?.id, displayTitle]
  );

  // Заявка с витринного калькулятора несёт ЕГО расчёт (#1335): иначе в CRM
  // уходили бы цифры BF-займа, которых покупатель не видел. Ключи формы и
  // MODX-полей прежние — это идентификаторы потока, их не трогаем (#1259).
  const quoteDeposit = financeQuote ? financeQuote.deposit : deposit;
  const quoteFinanced = financeQuote ? financeQuote.loan : financedAmount;

  const financeAttrs = React.useMemo(
    () => ({
      formTemplateCode: 'bf_finance',
      formType: 'finance',
      formTitle: 'Request a Financing Offer for {car}',
      carId: car?.id,
      carTitle: displayTitle,
      presentation: 'modal',
      formModalOpen: true,
      formExtra: {
        carPrice: displayPrice || undefined,
        deposit: quoteDeposit || undefined,
        financeAmount: quoteFinanced || undefined,
      },
      // EXACT legacy BF-Finance MODX payload — snake_case keys, `€…` formatting,
      // enquiry_type, monthly computed with the dealer-configured calc.
      formModxFields: ({ formData, sourceUrl }) => {
        const pcm = financeQuote
          ? financeQuote.monthly
          : calculateMonthlyPayment(financedAmount);
        return {
          formEnquire: 'BF Finance Application',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || '',
          position: displayTitle || '',
          enquiry_type: 'bf_finance',
          car_price: displayPrice ? `€${displayPrice.toLocaleString()}` : '',
          deposit: `€${quoteDeposit.toLocaleString()}`,
          finance_amount: `€${quoteFinanced.toLocaleString()}`,
          monthly_payment: pcm ? `€${pcm}/month` : '',
          nospam: '',
          url: sourceUrl,
        };
      },
    }),
    [
      car?.id,
      displayTitle,
      displayPrice,
      quoteDeposit,
      quoteFinanced,
      financeQuote,
      financedAmount,
      calculateMonthlyPayment,
    ]
  );

  // Set document.title to the car's name — Chrome derives the default
  // print-to-PDF filename from this. Sanitised for filesystem safety
  // (kill slashes / colons that would break naming on Windows + macOS).
  React.useEffect(() => {
    if (!car) return undefined;
    const prev = document.title;
    const raw = car.name || carTitle(car);
    const safe = raw
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (safe) document.title = safe;
    return () => {
      document.title = prev;
    };
  }, [car]);

  // Загружаем оптимизированные изображения
  React.useEffect(() => {
    if (!car || !car.images || car.images.length === 0) return;

    const generateOptimizedImages = async () => {
      try {
        // Параллельная загрузка всех URL'ов
        const mainPromises = car.images.map((path) =>
          generateImgproxyUrl(path, 800, 600)
        );
        const thumbnailPromises = car.images.map((path) =>
          generateImgproxyUrl(path, 250, 200)
        );

        const [mainImages, thumbnailImages] = await Promise.all([
          Promise.all(mainPromises),
          Promise.all(thumbnailPromises),
        ]);

        setOptimizedImages({
          main: mainImages,
          thumbnails: thumbnailImages,
        });
      } catch (err) {
        console.error('Error generating optimized images:', err);
      }
    };

    generateOptimizedImages();
  }, [car]);

  // Определяем мобильное устройство
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Пока открыта карточка авто — запоминаем её как "текущую", чтобы chat-init
  // из плавающего виджета атрибутировался к этой машине (#970).
  React.useEffect(() => {
    if (!car) return undefined;
    setCurrentCar(car);
    return () => setCurrentCar(null);
  }, [car]);

  // Функция для открытия виджета в текстовом режиме с контекстом авто (#568)
  const handleOpenWidget = () => {
    emitCtaClick(car, CtaType.ASK_AI);
    const launchCtx = car
      ? {
          action: 'ask_about_car',
          metadata: {
            carId: car.id,
            carTitle: displayTitle,
            price: displayPrice,
            make: car.make,
            model: car.model,
            year: car.year,
          },
        }
      : undefined;

    // Используем SDK API если доступен (widget режим на внешнем сайте)
    if (window.ShiftGearsWidget) {
      window.ShiftGearsWidget.open('chat', launchCtx);
    } else {
      // Fallback на внутренний API (standalone режим)
      openChat({ mode: 'chat', ...launchCtx });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="car-detail__state">
        <div className="car-detail__centered">
          <Loader2 size={32} className="car-detail__spinner" />
          <p className="car-detail__muted">
            {t('showcase.car.loading', 'Loading car details...')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="car-detail__state">
        <div className="car-detail__centered">
          {/* Технический текст ошибки — в консоли (CarsContext), не в UI. */}
          <p className="car-detail__error">
            {t(
              'showcase.car.loadError',
              'Couldn\u2019t load this car. Please try again.'
            )}
          </p>
          <Button onClick={() => window.location.reload()}>
            {t('showcase.car.retry', 'Retry')}
          </Button>
        </div>
      </div>
    );
  }

  // Car not found
  if (!car) {
    return (
      <div className="car-detail__state">
        <div className="car-detail__centered">
          <h1 className="car-detail__heading">
            {t('showcase.car.notFound', 'Car not found')}
          </h1>
          <Button onClick={handleBackToCars}>
            {t('showcase.car.back', 'Back to cars')}
          </Button>
        </div>
      </div>
    );
  }

  const engineCC = parseEngineCC(car.engineDisplacement);
  // Витринный калькулятор (#1335) — только на собственном стоке витрины.
  // Флаг в env контейнера demo.shiftgears.ai, а там же по токену открываются
  // и страницы дилеров: без `IS_OWN_STOCK` калькулятор достался бы им.
  // Витрина говорит на языке покупателя: цена по локали («15 990 €» во fr),
  // «по запросу» переведено, старая цена — только если она выше текущей
  // (#1335). У дилеров — прежние строки до символа.
  const formatPagePrice = config.IS_OWN_STOCK
    ? (value) => formatEuro(value, language)
    : (value) => `€${value.toLocaleString()}`;
  const priceOnRequestText = config.IS_OWN_STOCK
    ? t('showcase.car.priceOnRequest', 'Price on request')
    : 'Contact for price';
  const showOldPrice = config.IS_OWN_STOCK
    ? displayOldPrice > displayPrice
    : displayOldPrice && displayOldPrice > 0;
  // Разделы страницы под H1 — H2 на витрине. У дилеров H3, как было: под
  // ним их стили и печать.
  const SectionTitle = config.IS_OWN_STOCK ? 'h2' : 'h3';
  const showShowcaseFinance =
    config.IS_OWN_STOCK &&
    config.SHOW_FINANCE_CALCULATOR &&
    displayPrice > 0 &&
    !isDutyFree;
  // '' when the dealer hasn't stated a delivery date, or when the stated month
  // has already gone by — the pill is then omitted entirely (#1219).
  const deliveryText = formatDeliveryDate(car);

  // Блоки страницы — отдельными переменными: из них собираются две раскладки.
  // Дилерская (виджет, страницы по токену) — ровно в прежнем порядке и
  // разметке. Витрина ShiftGears (`IS_OWN_STOCK`) — вариант A из #1335:
  // слева галерея и разделы, справа закреплённая колонка с ценой и
  // действиями. Дилеров новая раскладка не касается.
  const logoEl = (
    <>
    {config.DEALER_LOGO_URL && (
      <div className="car-detail__print-only car-detail__print-only--flex">
        <img
          src={config.DEALER_LOGO_URL}
          alt=""
          className="car-detail__select"
        />
      </div>
    )}
    </>
  );
  const breadcrumbsEl = (
    <>
    {/* Крошки вместо ссылки «назад»: ссылка отвечает на вопрос «как
        уйти», крошки — на «где я». Покупатель попадает сюда из каталога, из
        поиска или по прямой ссылке из мессенджера, и в последнем случае
        «назад» вести некуда.

        «Catalog» — кнопка, а не ссылка: возврат несёт сохранённые фильтры
        (`handleBackToCars`), это переход с состоянием. */}
    <Breadcrumbs
      className="car-detail__no-print"
      items={[
        {
          label: t('showcase.nav.catalog', 'Catalog'),
          onClick: handleBackToCars,
        },
        { label: displayTitle },
      ]}
    />
    </>
  );
  const galleryEl = (
    <>
    {/* Image Gallery */}
    <div className="car-detail-image">
      {/* On screen: just the main photo. On print: 2 equal-width photos
          side by side (main + second). See @media print rules in
          styles/print.css. */}
      <div className="car-detail__block car-detail__print-row">
        <div
          className="car-detail__stage"
          {...(hasMultipleImages ? swipeHandlers : {})}
          style={hasMultipleImages ? { touchAction: 'pan-y' } : undefined}
        >
          {optimizedImages.main &&
          optimizedImages.main.length > 0 &&
          optimizedImages.main[currentImageIndex] ? (
            <img
              src={optimizedImages.main[currentImageIndex]}
              alt={displayTitle}
              className="car-detail__image car-detail__image--nodrag"
              draggable={false}
            />
          ) : (
            <CarImagePlaceholder
              make={car.make}
              model={car.model}
              className="car-detail__fill"
            />
          )}

          {/* Метки на фото, в левом верхнем углу — как на карточке
              каталога и тем же атомом: покупатель переходит с карточки, и
              «промо» должно стоять там же, где он его видел (#1335).
              Только витрина: у дилеров метки остаются над названием. */}
          {config.IS_OWN_STOCK &&
            (car.isHotOffer || car.statusSold?.toLowerCase() === 'yes') && (
              <div className="car-detail__ribbons">
                {car.isHotOffer && (
                  <CardRibbon variant="hot">
                    <Flame size={12} weight="bold" fill="currentColor" />
                    {showcaseText('showcase.car.hotOffer', 'HOT OFFER')}
                  </CardRibbon>
                )}
                {car.statusSold?.toLowerCase() === 'yes' && (
                  <CardRibbon variant="sold">
                    {showcaseText('landing.card.reserved', 'RESERVED')}
                  </CardRibbon>
                )}
              </div>
            )}

          {/* Desktop hover arrows — mirrors the catalog card. Hidden on
              print. pointer-events-none until hover so on touch devices
              (no hover) they don't intercept edge taps — the touch falls
              through to the swipe handler. Mobile browses via swipe +
              thumbnails. */}
          {hasMultipleImages && (
            <>
              <button
                type="button"
                onClick={prevImage}
                className="car-detail__nav car-detail__nav--prev car-detail__no-print"
                style={{ transform: 'translateY(-50%)' }}
                aria-label={t('showcase.car.prevImage', 'Previous image')}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={nextImage}
                className="car-detail__nav car-detail__nav--next car-detail__no-print"
                style={{ transform: 'translateY(-50%)' }}
                aria-label={t('showcase.car.nextImage', 'Next image')}
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>

        {/* Print-only: 2nd photo of the same size next to the main one. */}
        {optimizedImages.main && optimizedImages.main[1] && (
          <div className="car-detail__print-only car-detail-print-secondary">
            <img
              src={optimizedImages.main[1]}
              alt=""
              className="car-detail__image"
            />
          </div>
        )}
      </div>
      {optimizedImages.thumbnails &&
        optimizedImages.thumbnails.length > 1 && (
          <div className="car-detail__thumbs car-detail__no-print">
            {optimizedImages.thumbnails.map((image, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentImageIndex(index)}
                // preventDefault на mousedown гасит фокус кнопки при клике
                // мышью → браузер не делает auto-scroll «focused element into
                // view» к thumbnail'у ниже вьюпорта (прыжок страницы вниз при
                // первом клике). onClick всё равно срабатывает, а клавиатурный
                // фокус (Tab + Enter) не затрагивается.
                onMouseDown={(e) => e.preventDefault()}
                // Выбранная миниатюра — КЛАССОМ, а не инлайновым
                // `boxShadow`: инлайн нельзя ни перекрыть темой, ни
                // погасить под `prefers-reduced-motion`, и он был
                // единственным местом на странице, где вид задавался
                // из JS.
                className={`car-detail__thumb${
                  currentImageIndex === index
                    ? ' car-detail__thumb--current'
                    : ''
                }`}
                aria-current={currentImageIndex === index}
              >
                {image ? (
                  <img
                    src={image}
                    alt={`${displayTitle} view ${index + 1}`}
                    className="car-detail__image"
                  />
                ) : (
                  <CarImagePlaceholder
                    make={car.make}
                    model={car.model}
                    className="car-detail__fill"
                  />
                )}
              </button>
            ))}
          </div>
        )}
      {/* Chassis / VIN — technical id, intentionally low-key: small grey
          italic line under the photos, not a headline spec. */}
      {car.vin && (
        <p className="car-detail__fineprint car-detail__fineprint--italic">
          id: {car.vin}
        </p>
      )}
    </div>
    </>
  );
  const titleEl = (
    <>
    <div className="car-detail__block car-detail-title-block print-col-left">
      {/* Status badges (HOT OFFER, RESERVED). На витрине они на фото —
          см. `galleryEl`. */}
      {!config.IS_OWN_STOCK &&
        (car.isHotOffer || car.statusSold?.toLowerCase() === 'yes') && (
        <div className="car-detail__badges car-detail-badges">
          {/* Flame matches the Special Offers filter toggle, so the badge
              and the control that filters for it read as the same thing.
              `currentColor` (white) rather than the filter's amber: the
              badge is a --theme-primary FILL, and amber on Andys' red is
              2.19:1 — below the 3:1 icons need. White holds up on every
              brand primary (4.7:1 on that red, 8.7:1 on the demo blue). */}
          {car.isHotOffer && (
            <Badge variant="hot">
              <Flame size={12} weight="bold" fill="currentColor" />
              {showcaseText('showcase.car.hotOffer', 'HOT OFFER')}
            </Badge>
          )}
          {car.statusSold?.toLowerCase() === 'yes' && (
            <Badge variant="sold">{showcaseText('landing.card.reserved', 'RESERVED')}</Badge>
          )}
        </div>
      )}
      {/* Название и «поделиться» — одной строкой, кнопка у правого
          края. Раньше она стояла в конце ряда CTA, между «спросить» и
          «проверить наличие»: там она читалась третьим действием
          наравне с ними, хотя это не шаг покупки, а способ показать
          машину кому-то ещё. У заголовка — то место, где её ищут на
          любой доске объявлений.
          Печать кнопку не берёт: строка заголовка на листовку идёт, а
          ссылка «поделиться» на бумаге не значит ничего. */}
      <div className="car-detail__title-row">
        <h1 className="car-detail__title car-detail-title">
          {displayTitle}
        </h1>
        {/* Витрина (#1335, вариант «R3»): «поделиться» — маленькой круглой
            иконкой у названия на любой ширине; на телефоне по клику
            открывается системное меню, как и раньше. */}
        {(!isMobile || config.IS_OWN_STOCK) && (
          <ShareButton
            car={car}
            title={displayTitle}
            imageUrl={optimizedImages.main?.[0]}
            price={displayPrice}
            year={car.year}
            mileage={car.mileage}
            tagline={config.SHARE_TAGLINE}
            isMobile={isMobile}
            iconOnly={config.IS_OWN_STOCK}
            className="car-detail__no-print"
          />
        )}
      </div>
    </div>
    </>
  );
  const priceRowEl = (
    <>
    {/* Price block — its own sibling so that on print it can
        land in the right column (next to title-block which
        goes left). On screen it sits below the title as
        before. */}
    {/* Screen-only price row (with duty-free toggle). Hidden on
        print; the print page uses the bordered price-blocks
        grid below. */}
    <div className="car-detail__price-row car-detail-price-row car-detail__no-print">
      <div className="car-detail__col">
        {showOldPrice && (
          <span className="car-detail__price-old">
            {formatPagePrice(displayOldPrice)}
          </span>
        )}
        <div className="car-detail__row">
          {/* Витрина: цена чёрная (#1335) — синий на сайте только у главного
              действия; у дилера прежний бренд-цвет. */}
          <p
            className="car-detail__price"
            style={
              config.IS_OWN_STOCK
                ? undefined
                : { color: 'var(--theme-primary, #2142FF)' }
            }
          >
            {displayPrice > 0
              ? formatPagePrice(displayPrice)
              : priceOnRequestText}
          </p>
          {/* new-model (Autodis): "+reg" не показываем — цена финальная (#1086). */}
          {displayPrice > 0 &&
            !car.registrationStatus &&
            !car.isNewModel && <RegFeeLabel govFeeText={govFeeText} />}
        </div>
      </div>
      {config.SHOW_DUTY_FREE_FILTER && car.price > 0 && (
        <DutyFreeCheckbox />
      )}

    </div>
    </>
  );
  // Витринный калькулятор платежа (#1335). В колонке цены — тизер: платёж и
  // APR, по клику прокрутка к разделу «Финансирование». Сам калькулятор —
  // отдельным разделом в основной колонке: в узкой колонке он растягивал её
  // на два экрана. Флаг есть только у витрины — дилерский BF-блок не тронут.
  const FINANCE_SECTION_ID = 'car-finance';
  const teaserQuote =
    liveQuote ||
    (showShowcaseFinance
      ? calculateFinance({
          price: displayPrice,
          deposit: depositRange(displayPrice).initial,
          months: SHOWCASE_FINANCE.defaultTerm,
        })
      : null);
  const financeEl = (
    <>
    {showShowcaseFinance && (
      <div className="car-detail__finance car-detail__no-print">
        <FinanceTeaser
          quote={teaserQuote}
          href={`#${FINANCE_SECTION_ID}`}
          onClick={(event) => {
            // HashRouter: якорь в адресе сменил бы маршрут — скроллим сами.
            // Плавно — только без «уменьшить движение»; фокус переводим на
            // раздел, иначе клавиатура и читалка остаются на тизере.
            event.preventDefault();
            const section = document.getElementById(FINANCE_SECTION_ID);
            if (!section) return;
            const reduceMotion = window.matchMedia?.(
              '(prefers-reduced-motion: reduce)'
            ).matches;
            section.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'start',
            });
            section.focus({ preventScroll: true });
          }}
        />
      </div>
    )}
    </>
  );
  const financeSectionEl = (
    <>
    {showShowcaseFinance && (
      <Card
        className="car-detail__block car-detail__finance-section"
        id={FINANCE_SECTION_ID}
        tabIndex={-1}
      >
        <CardContent className="car-detail__card-body">
          <SectionTitle className="car-detail__section-title">
            {showcaseText('showcase.car.financing', 'Financing')}
          </SectionTitle>
          <FinanceCalculator
            price={displayPrice}
            onQuoteChange={setLiveQuote}
            onRequestOffer={(quote) => {
              setFinanceQuote(quote);
              emitCtaClick(car, CtaType.BF_FINANCE);
              setIsFinanceModalOpen(true);
            }}
          />
        </CardContent>
      </Card>
    )}
    </>
  );
  const printPricesEl = (
    <>
    {/* Print-only: 2 bordered price blocks side by side under
        the title. Regular price on the left, British Forces
        duty-free on the right. All visual sizing lives in
        styles/print.css (.print-price-card / .print-price-amount /
        .print-fee-line / .print-bf-label) — keep markup
        presentation-free. */}
    <div className="car-detail__print-only car-detail__print-only--grid print-price-blocks">
      <div className="print-price-card">
        <div className="car-detail__row">
          <p className="car-detail__print-amount print-price-amount print-price-amount--primary">
            {car.price > 0
              ? `€${car.price.toLocaleString()}`
              : 'Contact for price'}
          </p>
          {car.price > 0 && !car.registrationStatus && (
            <RegFeeLabel govFeeText={govFeeText} />
          )}
        </div>
        {/* Fee footnote lives in the left (main) price card. */}
        {govFeeText && !car.registrationStatus && (
          <div className="print-fee-line">
            * {govFeeText.replace(/^\*\s*/, '')}
          </div>
        )}
      </div>
      {config.SHOW_DUTY_FREE_PRICES &&
        car.dutyFree &&
        car.dutyFreePrice > 0 &&
        car.dutyFreePrice !== car.price && (
          <div className="print-price-card print-price-card--bf">
            <div className="car-detail__row">
              <p className="car-detail__print-amount print-price-amount">
                €{car.dutyFreePrice.toLocaleString()}
              </p>
              {!car.registrationStatus && (
                <RegFeeLabel govFeeText={govFeeText} />
              )}
            </div>
            <div className="print-bf-label">
              {/* Strip trailing "s" from "Prices" → "Price" on
                  the print label (singular reads better next to
                  a single value). */}
              {(labelText || 'Duty Free Price').replace(
                /Prices$/,
                'Price'
              )}
            </div>
            {/* Fee footnote moved to the main (left) price card. */}
          </div>
        )}
    </div>
    </>
  );
  const bfPanelEl = (
    <>
    {/* British Forces Exclusive Package — дилерский путь, раскрывается
        переключателем Duty Free. На витрине вместо него
        `FinanceCalculator` выше (#1335). */}
    {config.SHOW_BF_PACKAGE && !showShowcaseFinance && displayPrice > 0 && (
      <div
        className={`car-detail__collapsible print-col-right${
          isDutyFree ? ' car-detail__collapsible--open' : ''
        }`}
      >
        <div className="car-detail__clip">
          <Card className="car-detail__card--muted">
            <CardContent className="car-detail__card-body--responsive">
              {/* BF price moved to the bordered price-blocks grid
                  near the top of the page. */}

              <h3 className="car-detail__section-title car-detail__section-title--tight">
                {bfPackageTitle.replace(/\\n|\n/g, ' ')}
              </h3>

              <div className="car-detail__price-baseline">
                <span className="car-detail__hint">
                  {showcaseText('showcase.car.financeFrom', 'from')}
                </span>
                <p className="car-detail__price car-detail__price--accent">
                  €{monthlyPayment.toLocaleString()}
                  <span className="car-detail__price-note">
                    /mo*
                  </span>
                </p>
              </div>

              {/* Deposit selector — слайдер + ручной ввод.
                  Минимум = price - maxFinancing (всё, что не помещается
                  в €12,000 финансирования). Увеличение deposit'а сверх
                  минимума уменьшает финансируемую сумму и PCM.
                  Label слева, поле ввода справа; значение — в brand-цвете. */}
              <div className="car-detail__gap">
                {/* Подпись, поле и ползунок — ОДНОЙ строкой: ползунок
                    меняет ровно то число, что стоит в поле, и
                    отдельной строкой под ним читался самостоятельным
                    контролом. Ниже остаются только концы шкалы. */}
                <div className="car-detail__row car-detail__row--wide car-detail__deposit">
                  <label
                    htmlFor="bf-deposit"
                    className="car-detail__hint"
                  >
                    {showcaseText('showcase.car.downPaymentLabel', 'Down payment')}
                  </label>
                  <div className="car-detail__stepper">
                    <span className="car-detail__label">€</span>
                    <input
                      id="bf-deposit"
                      type="text"
                      inputMode="numeric"
                      // On mobile the field is display-only (the slider is
                      // the input); readOnly stops iOS from raising the
                      // keyboard on stray taps. Mirrors the catalog
                      // combobox pattern (readOnly on mobile).
                      readOnly={isMobile}
                      value={depositDisplay}
                      onChange={(e) =>
                        handleDepositInput(e.target.value)
                      }
                      onFocus={() => {
                        if (!isMobile) setDepositFocused(true);
                      }}
                      onBlur={handleDepositBlur}
                      aria-label={t(
                        'showcase.car.downPayment',
                        'Down payment amount'
                      )}
                      className="car-detail__stepper-input"
                    />
                  </div>
                  <Slider
                    className="car-detail__deposit-slider"
                    min={minDeposit}
                    max={displayPrice}
                    step={100}
                    value={deposit}
                    onChange={(e) => handleSliderChange(e.target.value)}
                    style={{
                      ['--slider-thumb-color']: bfSliderColorResolved,
                      ['--slider-thumb-hover-color']:
                        bfSliderColor || '#000000',
                    }}
                  />
                </div>
                <div className="car-detail__scale">
                  <span>Min €{minDeposit.toLocaleString()}</span>
                  <span>
                    {`${showcaseText('showcase.car.financing', 'Financing')} €${financedAmount.toLocaleString()}`}
                  </span>
                </div>
              </div>

              <div className="car-detail__toggle-row">
                {[
                  bfPackageBadge1,
                  bfPackageBadge2,
                  bfPackageBadge3,
                ].map((badge, i) => (
                  <span
                    key={i}
                    className="car-detail__pill"
                    style={{
                      border: `1px solid ${bfBadgeColorResolved}`,
                      color: bfBadgeColorResolved,
                    }}
                  >
                    {badge}
                  </span>
                ))}
              </div>

              {/* Дисклеймер - по центру. На печать: левый край,
                  плюс ниже строка с government fee. */}
              <p className="car-detail__fineprint">
                {bfPackageDisclaimer}
                {govFeeText && (
                  <span className="car-detail__print-only car-detail__print-only--inline">
                    <br />
                    {govFeeText.replace(/^\*\s*/, '* ')}
                  </span>
                )}
              </p>
              {/* Сноска к бейджу с маркером "**" — показываем только если
                  бейдж его реально содержит (бейдж конфигурируем per-org,
                  может не иметь "**" → иначе сноска осиротеет). */}
              {bfPackageBadge1?.includes('**') && (
                <p className="car-detail__fineprint">
                  {showcaseText('showcase.car.carsPriced', '** For cars priced €12,000 or below')}
                </p>
              )}

              {/* CTA кнопка — открывает лид-форму.
                  Гармонизирована с main CTAs ниже: size="lg" + .cta-button.
                  На мобилке escape CardContent padding (24px per side —
                  CardContent's default `p-6` wins over our `p-4` override
                  due to class order) чтобы кнопка по ширине совпала с
                  "Check Availability" / "Call Now" ниже.  */}
              <Button
                size="lg"
                className="cta-button car-detail__cta--bleed car-detail__no-print"
                onClick={() => {
                  setFinanceQuote(null);
                  emitCtaClick(car, CtaType.BF_FINANCE);
                  setIsFinanceModalOpen(true);
                }}
              >
                {bfPackageCtaText}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )}
    </>
  );
  // Витрина (#1335, вариант «R3»): характеристики — метками «Année 08/2024»,
  // без двоеточия; у дилера прежнее «Year: 08/2024».
  const specColon = config.IS_OWN_STOCK ? '' : ':';
  const specLabel = (label) =>
    config.IS_OWN_STOCK ? label.replace(/\s*:\s*$/, '') : label;
  const specsEl = (
    <>
    {/* Key Specs. On print, sits in the right column next to
        the stacked price blocks on the left. */}
    <Card className="car-detail__block print-cell-specs print-col-right">
      <CardContent className="car-detail__card-body">
        <div className="car-detail__specs">
          <div className="car-detail__row">
            <Calendar size={20} className="car-detail__icon" />
            <span className="car-detail__muted">
              {t('showcase.car.year', 'Year')}{specColon}
            </span>
            <span className="car-detail__label">
              {formatRegYear(car)}
            </span>
          </div>
          <div className="car-detail__row">
            <MapPin size={20} className="car-detail__icon" />
            <span className="car-detail__muted">
              {t('showcase.car.mileage', 'Mileage')}{specColon}
            </span>
            <span className="car-detail__label">
              {car.mileage > 0
                ? `${
                    config.IS_OWN_STOCK
                      ? car.mileage.toLocaleString(numberLocale(language))
                      : car.mileage.toLocaleString()
                  } km`
                : 'N/A'}
            </span>
          </div>
          <div className="car-detail__row">
            <Fuel size={20} className="car-detail__icon" />
            <span className="car-detail__muted">
              {t('showcase.car.fuel', 'Fuel')}{specColon}
            </span>
            <span className="car-detail__label">
              {translateVehicleValue(car.fuelType, 'fuelType')}
            </span>
          </div>
          <div className="car-detail__row">
            <Cog size={20} className="car-detail__icon" />
            <span className="car-detail__muted">
              {t('showcase.car.transmission', 'Transmission')}{specColon}
            </span>
            <span className="car-detail__label">
              {translateVehicleValue(car.transmission, 'transmission')}
            </span>
          </div>
          {engineCC && (
            <div className="car-detail__row">
              <Gauge size={20} className="car-detail__icon" />
              <span className="car-detail__muted">
                {t('showcase.car.engine', 'Engine')}{specColon}
              </span>
              <span className="car-detail__label">{engineCC} cc</span>
            </div>
          )}
          {car.warrantyTerm && (
            <div className="car-detail__row">
              <ShieldCheck
                size={20}
                className="car-detail__icon car-detail__icon--accent"
              />
              <span className="car-detail__muted">
                {t('showcase.car.warranty', 'Warranty')}{specColon}
              </span>
              <span className="car-detail__label">
                {car.warrantyTerm}
              </span>
            </div>
          )}

          {/* Расход, CO₂ и запас хода (#1330). Данные приходят из
              инвентаря давно, но карточка их не показывала — при том
              что в Европе это первое, что покупатель ищет глазами.
              Блок выключен по умолчанию: он добавляет строки, то есть
              меняет вид клиентской поверхности. Дилер включает сам. */}
          <VehicleEfficiency
            car={car}
            enabled={config.SHOW_EFFICIENCY_DISCLOSURE}
            hideEmpty={config.IS_OWN_STOCK}
            labels={{
              consumption: specLabel(showcaseText('showcase.car.consumption', 'Consumption:')),
              co2: specLabel(showcaseText('showcase.car.co2', 'CO₂ emissions:')),
              range: specLabel(showcaseText('showcase.car.range', 'Electric range:')),
            }}
          />
        </div>

        {/* Статус наличия (#1219, расширен в #1330) — залитый бейдж в
            ПОДВАЛЕ карточки характеристик, а не строка в сетке: среди
            восьми характеристик его не читали, но это всё-таки
            характеристика, поэтому он остаётся в карточке, а не висит
            между ней и кнопками. Внутри карточки он ещё и наследует её
            печатную обработку (`.print-cell-specs`).

            Источников два, приоритет у структурной даты — см. JSDoc
            `AvailabilityStatus`. Ни даты, ни текста — бейджа нет:
            отсутствие означает «неизвестно», а заглушка читалась бы как
            обещание. */}
        {(deliveryText ||
          (config.SHOW_AVAILABILITY_STATUS && car.availability)) && (
          <div className="car-detail__gap--top">
            <AvailabilityStatus
              car={car}
              showStatedAvailability={config.SHOW_AVAILABILITY_STATUS}
            />
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
  const classifiedsEl = (
    <>
    {/* «Эта же машина на площадках» (#1338) — рядом с кнопками: это
        такой же переход, как «позвонить» или «спросить», а не отдельный
        раздел.

        ТОЛЬКО на витрине, и это не флаг, а условие. Блок называет
        чужие площадки (Bazaraki и прочие) — дилеру на его собственном
        сайте он предлагает уйти к конкуренту, и включить его случайной
        настройкой в widget-config не должно быть возможно вовсе.
        Флаг остаётся сверху: на витрине блок тоже показывается не
        всегда. */}
    {config.ENVIRONMENT_NAME !== 'widget' &&
      config.SHOW_CLASSIFIEDS_SYNC && (
        <div className="car-detail__no-print">
          {/* На витрине без «Synced today»: покупателю статус синхронизации
              ни о чём не говорит, это кухня дилера (#1335). */}
          <ClassifiedsSync status={config.IS_OWN_STOCK ? '' : undefined} />
        </div>
      )}
    </>
  );
  const ctaEl = (
    <>
    {/* CTA Buttons. Раньше они пропадали, пока открыт калькулятор:
        тумблер работал как режим, и «Check Availability» рядом с
        расчётом перетягивал на другой разговор. Тумблера больше нет,
        калькулятор — обычный блок страницы, и прятать за ним кнопки
        значило бы оставить страницу без единого целевого действия. */}
    <div
      className="car-detail__cta-row car-detail__no-print"
      ref={config.IS_OWN_STOCK ? setCtaRowEl : undefined}
    >
      <Button
        size="lg"
        className="cta-button car-detail__cta"
        onClick={() => {
          emitCtaClick(car, CtaType.CHECK_AVAILABILITY);
          setIsModalOpen(true);
        }}
      >
        {showcaseText('showcase.car.checkAvailability', 'Check Availability')}
      </Button>
      {/* Secondary CTA: on desktop always Ask AI; on mobile Call during
          business hours, Ask AI after-hours (so the user always has a
          path forward — no dead "Call Now" at 11pm). */}
      {(() => {
        const showCallOnMobile =
          isMobile && config.SHOW_CALL_BUTTON && contactInfo.isOpen;
        const showAiButton =
          config.SHOW_TALK_AI_BUTTON && !showCallOnMobile;

        if (showCallOnMobile && config.IS_OWN_STOCK) {
          // Витрина: подпись по языку и одна кнопка-ссылка вместо
          // `<a><button>` — два вложенных интерактивных элемента (#1335).
          return (
            <Button
              as="a"
              href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
              size="lg"
              variant="outline"
              arrow
              className="car-detail__cta--block"
            >
              {showcaseText('showcase.car.callNow', 'Call Now', config.CALL_BUTTON_TEXT)}
            </Button>
          );
        }
        if (showCallOnMobile) {
          return (
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>
              <Button
                size="lg"
                variant="outline"
                className="car-detail__cta--block"
              >
                {config.CALL_BUTTON_TEXT}
              </Button>
            </a>
          );
        }
        if (showAiButton) {
          return (
            <Button
              size="lg"
              variant="outline"
              className={
                isMobile ? 'car-detail__cta--block' : 'car-detail__cta'
              }
              onClick={handleOpenWidget}
            >
              {showcaseText('showcase.car.askQuestion', 'Ask a Question', config.TALK_AI_BUTTON_TEXT)}
            </Button>
          );
        }
        return null;
      })()}
      {/* На узком экране «поделиться» остаётся в ряду CTA: у
          заголовка для неё нет места, а `navigator.share` на телефоне
          открывает системный лист — это действие того же порядка, что
          «позвонить» и «написать». На десктопе кнопка уехала к
          заголовку (см. `car-detail__title-row` выше). */}
      {isMobile && !config.IS_OWN_STOCK && (
        <ShareButton
          car={car}
          title={displayTitle}
          imageUrl={optimizedImages.main?.[0]}
          price={displayPrice}
          year={car.year}
          mileage={car.mileage}
          tagline={config.SHARE_TAGLINE}
          isMobile
        />
      )}
    </div>
    </>
  );
  const descriptionEl = (
    <>
    {/* Описание и характеристики — ДВУМЯ плашками, как было до
        витрины. Их объединяли в одну («два взгляда на одну машину»),
        но карточка авто едет дилерам, и менять им раскладку страницы
        ради вида витрины нельзя: у них это два раздела и читаются они
        как два. */}
    <Card className="car-detail__block print-cell-description print-col-full">
      <CardContent className="car-detail__card-body">
        <SectionTitle className="car-detail__section-title">
          {t('showcase.car.description', 'Description')}
        </SectionTitle>
        {/* Описание приходит из инвентаря дилера (Sheets, фиды) —
            чужой текст, поэтому HTML после markdown чистим: без этого
            `<img onerror>` из ячейки таблицы исполнялся на странице. */}
        <div
          className="car-detail__prose"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              marked.parse(car.description || '')
            ),
          }}
        />
      </CardContent>
    </Card>
    </>
  );
  const featuresEl = (
    <>
    {car.features && car.features.length > 0 && (
      <Card className="car-detail__block print-cell-features print-col-full">
        <CardContent className="car-detail__card-body">
          <SectionTitle className="car-detail__section-title">
            {t('showcase.car.features', 'Features')}
          </SectionTitle>
          <ul className="car-detail__features">
            {car.features.map((feature, index) => (
              <li key={index} className="car-detail__feature">
                <Check
                  size={16}
                  className="car-detail__icon car-detail__icon--accent"
                  weight="bold"
                />
                <span>{translateVehicleValue(feature, 'features')}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    )}
    </>
  );
  const whyBuyEl = (
    <>
    {/* Why BUY from Us */}
    {config.SHOW_WHY_BUY_BLOCK && (
      <Card className="car-detail__block print-col-full">
        <CardContent className="car-detail__card-body">
          <SectionTitle className="car-detail__section-title">
            {config.WHY_BUY_TITLE}
          </SectionTitle>
          <ul className="car-detail__stack">
            {[
              config.WHY_BUY_ITEM_1,
              config.WHY_BUY_ITEM_2,
              config.WHY_BUY_ITEM_3,
              config.WHY_BUY_ITEM_4,
              config.WHY_BUY_ITEM_5,
            ]
              .filter(Boolean)
              .map((item, index) => (
                <li key={index} className="car-detail__row">
                  <ArrowRight
                    size={20}
                    className="car-detail__icon car-detail__icon--accent"
                  />
                  <span>{item}</span>
                </li>
              ))}
          </ul>
        </CardContent>
      </Card>
    )}
    </>
  );

  const modalsEl = (
    <>
      {/* Check Availability — template-driven LeadFormEmbed in modal
          presentation (#1087). Mounted only while open; auto-opens the sheet
          and unmounts on close. Captures the lead to CRM, fires the optional
          MODX post, then morphs form→chat in place. */}
      {isModalOpen && (
        <LeadFormEmbed
          attrs={checkAvailabilityAttrs}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {/* BF Finance Application — same template-driven LeadFormEmbed; passes the
          real financed figures via formExtra so the monthly is computed with the
          dealer-configured balloon calc and flows to CRM + the MODX post. */}
      {isFinanceModalOpen && (
        <LeadFormEmbed
          attrs={financeAttrs}
          onClose={() => setIsFinanceModalOpen(false)}
        />
      )}
    </>
  );

  if (config.IS_OWN_STOCK) {
    return (
      <div className="car-detail car-detail--showcase">
        <div className="car-detail__container">
          {logoEl}
          {breadcrumbsEl}
          <div className="car-detail__layout">
            <div className="car-detail__main">
              {galleryEl}
              {descriptionEl}
              {featuresEl}
              {financeSectionEl}
              {whyBuyEl}
            </div>
            <aside
              className="car-detail__rail"
              aria-label={t('showcase.car.priceAndActions', 'Price and actions')}
            >
              {/* Одна плитка (#1335, вариант «R3» с холста): название и
                  «поделиться», крупная цена, сбор строкой, платёж строкой,
                  характеристики метками, кнопки в строку, площадки тихими
                  ссылками. На широком экране плитка прилипает к шапке. */}
              <div className="car-detail__rail-card">
                {titleEl}
                {priceRowEl}
                {printPricesEl}
                {financeEl}
                {specsEl}
                {ctaEl}
                {classifiedsEl}
              </div>
            </aside>
          </div>
        </div>
        {modalsEl}
      </div>
    );
  }

  return (
    <div className="car-detail">
      <div className="car-detail__container">
        {logoEl}
        {breadcrumbsEl}
        <div className="car-detail__grid">
          {galleryEl}
          {/* Car Details. `car-details-col` is the print-time grid.
              Top section is 2-column on print: title + Specs on the left,
              price + BF Package on the right. Below the top section
              (Description, Features, Why Buy) spans full width. On
              screen the class is inert — children stack vertically. */}
          <div className="car-details-col">
            {titleEl}
            {priceRowEl}
            {financeEl}
            {printPricesEl}
            {bfPanelEl}
            {specsEl}
            {classifiedsEl}
            {ctaEl}
            {descriptionEl}
            {featuresEl}
            {whyBuyEl}
          </div>
        </div>

        {/* Page-bottom fee footnote removed — the same line now appears
            inside the BF Package disclaimer (only place it's shown to
            avoid duplication). */}
      </div>
      {modalsEl}
    </div>
  );
};

export default CarDetailPage;
